import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { SymptomChecker } from "@/components/content/SymptomChecker";
import { FixSteps } from "@/components/content/FixSteps";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";

const LandscapingNotGettingLeads = () => {
  const symptoms = [
    "You have a portfolio but no one is requesting quotes",
    "Visitors browse your gallery but don't contact you",
    "Your website traffic is decent but conversions are near zero",
    "Competitors with worse work are booking more jobs",
    "You rank for landscaping keywords but get no calls",
    "Your contact form gets spam but not real leads"
  ];

  const rootCauses = [
    { cause: "Portfolio showcases work but doesn't sell", impact: "Beautiful photos without context don't convince prospects" },
    { cause: "No pricing indicators or project ranges", impact: "Homeowners assume you're too expensive and leave" },
    { cause: "Missing before/after transformations", impact: "Can't visualize the value you provide" },
    { cause: "No clear process explanation", impact: "Prospects don't understand what working with you looks like" },
    { cause: "Generic testimonials without specifics", impact: "Reviews don't address concerns about reliability or quality" },
    { cause: "No service area clarity", impact: "Visitors unsure if you serve their neighborhood" },
    { cause: "Slow-loading image galleries", impact: "High-quality photos tank page speed and lose visitors" },
    { cause: "Quote form asks for too much detail", impact: "Overwhelmed prospects abandon the form" }
  ];

  const fixSteps = [
    { step: "Add project context to your portfolio", details: "Include project scope, timeline, and customer situation for each showcase" },
    { step: "Display pricing ranges for common services", details: "'Patio installation starts at $X' removes price anxiety and qualifies leads" },
    { step: "Create dramatic before/after galleries", details: "Side-by-side transformations show value better than finished photos alone" },
    { step: "Explain your process in 4-5 steps", details: "Consultation → Design → Installation → Completion timeline gives clarity" },
    { step: "Get specific testimonials with photos", details: "Ask customers to describe their problem, your solution, and the result" },
    { step: "Optimize images for fast loading", details: "Compress portfolio images to under 200KB each without losing quality" },
    { step: "Simplify your quote form to essentials", details: "Name, contact, project type, and optional photo upload is plenty" }
  ];

  const faqs = [
    { question: "Why isn't my landscaping website generating leads?", answer: "Landscaping websites often fail because they focus on showcasing work rather than selling services. Beautiful portfolios without pricing context, clear processes, and compelling calls-to-action don't convert visitors into leads." },
    { question: "Should landscaping websites show prices?", answer: "Yes, showing price ranges significantly increases leads. Visitors who see 'Patio installations from $5,000' are more likely to contact you than those facing a mystery price. It also pre-qualifies leads, saving you time on low-budget inquiries." },
    { question: "How important is my portfolio for getting leads?", answer: "Very important, but context matters more than beauty. Each portfolio piece should tell a story: what the customer needed, what you delivered, and what the result was. Add before/after photos and brief project descriptions." },
    { question: "What's the best way to display landscaping work?", answer: "Before/after transformations convert 3x better than finished-only photos. Use sliders or side-by-side comparisons. Include project details like size, timeline, and any challenges you overcame." },
    { question: "How long should my quote form be?", answer: "4-6 fields maximum. Name, phone, email, project type, and optional photo upload. Long forms asking for detailed project specifications before you've even spoken will kill conversions." },
    { question: "Do I need service area pages?", answer: "Absolutely. Service area pages help you rank for 'landscaper near [city]' searches and reassure visitors that you actually work in their area. Each page should have unique content relevant to that location." },
    { question: "Why do my competitors get more leads with worse work?", answer: "Better work doesn't equal better marketing. Competitors getting more leads likely have better website conversion elements: clearer CTAs, visible phone numbers, trust signals, and easier quote processes. Quality gets you referrals; marketing gets you new leads." },
    { question: "How can I make my landscaping website load faster?", answer: "Compress all portfolio images to under 200KB. Use lazy loading for galleries. Enable browser caching. Choose a fast hosting provider. Portfolio-heavy sites are prone to speed issues that kill conversions." },
    { question: "Should I include testimonials on my landscaping website?", answer: "Yes, but make them specific. Generic reviews like 'Great work!' don't convert. Testimonials that describe the customer's problem, your solution, and the transformation are much more persuasive." },
    { question: "What's the #1 thing killing my landscaping website conversions?", answer: "Usually it's the quote form or contact process. If visitors can't easily request a quote on mobile within 30 seconds, you're losing 60%+ of potential leads. Make your phone number clickable and your form simple." }
  ];

  const links = [
    { to: "/websites/", label: "Website Subscription Pricing" },
    { to: "/landscaping-website-design/", label: "Landscaping Website Design" },
    { to: "/growth-add-ons/gbp/", label: "Google Business Profile Optimization" },
    { to: "/service-pages-that-rank/", label: "Service Pages That Rank" }
  ];

  return (
    <>
      <SEO
        title="Landscaping Website Not Getting Leads? Here's Why | Leaf Rush Marketing"
        description="Your landscaping portfolio is stunning but the phone isn't ringing. Discover the real reasons and get actionable fixes to start generating leads."
        url="https://www.leafrushmarketing.com/website-fixes/landscaping-website-not-getting-leads/"
      />
      <PageSchema
        url="https://www.leafrushmarketing.com/website-fixes/landscaping-website-not-getting-leads/"
        title="Landscaping Website Not Getting Leads? Here's Why"
        description="Your landscaping portfolio is stunning but the phone isn't ringing. Discover the real reasons and get actionable fixes."
        faqs={faqs}
      />
      <LastUpdated />

      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Landscaping Website Not Getting Leads? Here's Why
            </h1>
            <p className="text-xl text-muted-foreground">
              Your portfolio showcases amazing transformations, but inquiries are nonexistent. Let's fix that.
            </p>
          </header>

          <TopCTA />

          <SymptomChecker symptoms={symptoms} title="Signs Your Landscaping Website Has a Conversion Problem" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Root Causes: Why Beautiful Portfolios Don't Convert</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {rootCauses.map((item, index) => (
                <div key={index} className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold text-foreground">{item.cause}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.impact}</p>
                </div>
              ))}
            </div>
          </section>

          <MidPageCTA />

          <FixSteps steps={fixSteps} title="7 Steps to Turn Your Portfolio Into a Lead Machine" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">The Portfolio Trap: Why Great Work Doesn't Equal Great Leads</h2>
            <div className="prose prose-lg max-w-none text-foreground">
              <p>
                Landscapers often invest heavily in professional photography, only to discover that stunning images alone don't generate leads. The problem? <strong>Portfolios showcase skill but don't sell services.</strong>
              </p>
              <p>
                A lead-generating landscaping website needs context around your work: pricing indicators, before/after comparisons, project timelines, and customer stories. Without these elements, visitors admire your work but don't take action.
              </p>
              <p>
                At <a href="/websites/" className="text-primary hover:underline">Leaf Rush Marketing</a>, we build landscaping websites that convert admirers into customers. Our designs combine visual impact with proven conversion elements that get your phone ringing.
              </p>
            </div>
          </section>

          <DIYvsDoneForYou />

          <FAQSection faqs={faqs} />

          <InternalLinks links={links} title="Related Resources" />

          <BottomCTA />

          <LeadFormSection title="Get Your Free Website Audit" />
        </div>
      </article>
    </>
  );
};

export default LandscapingNotGettingLeads;
